'use client';

import { c as _c } from "react/compiler-runtime";
import { Sparkles } from 'lucide-react';
import { useI18n } from '~/features/i18n';
import { Tabs } from '~/components/ui/tabs';
import { cn } from '~/utils';
import type { ActivitySearchMode } from './shared';
type Props = {
  mode: ActivitySearchMode;
  onChange: (mode: ActivitySearchMode) => void;
  /** Visual surface the control sits on. `light` = white bar, `glass` = dark hero. */
  theme?: 'light' | 'glass';
  /** `hero` caps the width; `sidebar` fills its container. */
  width?: 'hero' | 'full';
  className?: string;
  listRef?: React.Ref<HTMLDivElement>;
};

/**
 * The Filters / AI segmented control as a CONTROLLED component (mode + onChange come from the
 * caller). Shared by {@link ActivitySearchModeTabBar} (URL-driven, /teambuildings) and the
 * homepage block (localStorage-driven), so both render the same Base UI sliding indicator.
 */
export const SearchModeTabs: React.FC<Props> = t0 => {
  const $ = _c(57);
  const {
    mode,
    onChange,
    theme: t1,
    width: t2,
    className,
    listRef
  } = t0;
  const theme = t1 === undefined ? "glass" : t1;
  const width = t2 === undefined ? "hero" : t2;
  const i18n = useI18n();
  let T0;
  let T1;
  let T2;
  let t10;
  let t11;
  let t12;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== className || $[1] !== i18n || $[2] !== listRef || $[3] !== mode || $[4] !== onChange || $[5] !== theme || $[6] !== width) {
    const tabClass = cn("cursor-pointer relative z-10 flex min-h-0 flex-1 items-center justify-center rounded-full px-2.5 py-1.5 text-[13px] font-bold leading-none outline-none transition-colors sm:px-3", theme === "glass" && ["data-[active]:text-white hover:bg-white/10", "[&:not([data-active])]:text-white/65 [&:not([data-active])]:hover:text-white/90"], theme === "light" && ["data-[active]:text-white", "[&:not([data-active])]:text-gray-700 hover:bg-black/5"]);
    T2 = Tabs.Root;
    if ($[20] !== className) {
      t10 = cn("flex w-full flex-col items-center", className);
      $[20] = className;
      $[21] = t10;
    } else {
      t10 = $[21];
    }
    t11 = mode;
    if ($[22] !== onChange) {
      t12 = value => {
        if (value === "normal" || value === "ai") {
          onChange(value);
        }
      };
      $[22] = onChange;
      $[23] = t12;
    } else {
      t12 = $[23];
    }
    t8 = listRef;
    t9 = "flex w-full justify-center";
    T1 = Tabs.List;
    const t13 = theme === "glass" ? "bg-white/10" : "bg-black/5";
    const t14 = width === "hero" && "max-w-[220px]";
    if ($[24] !== t13 || $[25] !== t14) {
      t5 = cn("p-0.75 relative flex h-10 w-full shrink-0 rounded-full backdrop-blur-sm", t13, t14);
      $[24] = t13;
      $[25] = t14;
      $[26] = t5;
    } else {
      t5 = $[26];
    }
    const t15 = mode === "ai" ? "bg-yellow" : "bg-secondary";
    let t16;
    if ($[27] !== t15) {
      t16 = cn("pointer-events-none absolute left-[var(--active-tab-left)] top-[var(--active-tab-top)] z-0 h-[var(--active-tab-height)] w-[var(--active-tab-width)] rounded-full transition-[left,top,width,height] duration-200 ease-out", t15);
      $[27] = t15;
      $[28] = t16;
    } else {
      t16 = $[28];
    }
    if ($[29] !== t16) {
      t6 = <Tabs.Indicator className={t16} renderBeforeHydration={true} />;
      $[29] = t16;
      $[30] = t6;
    } else {
      t6 = $[30];
    }
    const t17 = cn(tabClass, "gap-0");
    let t18;
    if ($[31] !== i18n) {
      t18 = i18n._("activity.search.mode.filters", undefined, {
        message: "Filters"
      });
      $[31] = i18n;
      $[32] = t18;
    } else {
      t18 = $[32];
    }
    t7 = <Tabs.Tab value="normal" className={t17}>{t18}</Tabs.Tab>;
    T0 = Tabs.Tab;
    t3 = "ai";
    t4 = cn(tabClass, "gap-1.5", "data-[active]:text-yellow-950");
    $[0] = className;
    $[1] = i18n;
    $[2] = listRef;
    $[3] = mode;
    $[4] = onChange;
    $[5] = theme;
    $[6] = width;
    $[7] = T0;
    $[8] = T1;
    $[9] = T2;
    $[10] = t10;
    $[11] = t11;
    $[12] = t12;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    T0 = $[7];
    T1 = $[8];
    T2 = $[9];
    t10 = $[10];
    t11 = $[11];
    t12 = $[12];
    t3 = $[13];
    t4 = $[14];
    t5 = $[15];
    t6 = $[16];
    t7 = $[17];
    t8 = $[18];
    t9 = $[19];
  }
  let t13;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <Sparkles className="size-3 shrink-0" strokeWidth={2.25} aria-hidden={true} />;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== i18n) {
    t14 = i18n._("activity.search.mode.ai", undefined, {
      message: "AI Search"
    });
    $[34] = i18n;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== T0 || $[37] !== t14 || $[38] !== t3 || $[39] !== t4) {
    t15 = <T0 value={t3} className={t4}>{t13}{t14}</T0>;
    $[36] = T0;
    $[37] = t14;
    $[38] = t3;
    $[39] = t4;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  let t16;
  if ($[41] !== T1 || $[42] !== t15 || $[43] !== t5 || $[44] !== t6 || $[45] !== t7) {
    t16 = <T1 className={t5}>{t6}{t7}{t15}</T1>;
    $[41] = T1;
    $[42] = t15;
    $[43] = t5;
    $[44] = t6;
    $[45] = t7;
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  let t17;
  if ($[47] !== t16 || $[48] !== t8 || $[49] !== t9) {
    t17 = <div ref={t8} className={t9}>{t16}</div>;
    $[47] = t16;
    $[48] = t8;
    $[49] = t9;
    $[50] = t17;
  } else {
    t17 = $[50];
  }
  let t18;
  if ($[51] !== T2 || $[52] !== t10 || $[53] !== t11 || $[54] !== t12 || $[55] !== t17) {
    t18 = <T2 className={t10} value={t11} onValueChange={t12}>{t17}</T2>;
    $[51] = T2;
    $[52] = t10;
    $[53] = t11;
    $[54] = t12;
    $[55] = t17;
    $[56] = t18;
  } else {
    t18 = $[56];
  }
  return t18;
};